import React, { PropsWithChildren, useContext, useEffect } from 'react';
import FullScreenLoader from '@components/FullScreenLoader';
import {
  canRedirectTo,
  CURRENT_APP_VERSION,
  getOrganizationAppVersion,
  resolveAppVersion,
} from '@core/constants/appVersion';
import useAppVersionPreference from '@core/hooks/useAppVersionPreference';
import useGetUserPreferences from '@core/hooks/useGetUserPreferences';
import useOrganizationSettings from '@core/hooks/useOrganizationSettings';
import useProfile from '@core/hooks/useProfile';
import { AuthContext } from '@core/providers/AuthProvider';
import { RoleName } from '@core/types';
import { allowAutomaticAppSwitch } from '@core/utils/appSwitch';
import buildAppSwitchUrl from '@core/utils/buildAppSwitchUrl';

// Decides which app version (v2/legacy vs v3) the current user belongs on and
// bounces the browser to the other deployment when it doesn't match the one
// currently being served. Radiologists can pin their own preference from the
// header switch or the Preferences page; everyone else — including users who
// stopped being Radiologists, whose stored preference is then ignored —
// follows the organization-wide setting admins choose in Personalización
// (v2 when it was never set).
const AppVersionGuard = ({ children }: PropsWithChildren) => {
  const { organization } = useContext(AuthContext);
  const { role } = useProfile();
  const { preferredVersion } = useAppVersionPreference();
  const { isSuccess: isUserPreferencesReady } = useGetUserPreferences();
  const { organizationSettings, isSuccess: isOrganizationSettingsReady } =
    useOrganizationSettings();
  const organizationId = organization?.organizationId;

  // Don't decide on partial data: an unloaded organization setting would read
  // as the v2 default and bounce users before the real value arrives. If the
  // settings (or a Radiologist's preferences) fail to load we stay put rather
  // than guess — a missing preference would otherwise read as "no choice".
  const isRadiologist = role === RoleName.Radiologist;
  const isResolved =
    !!organizationId &&
    !!role &&
    isOrganizationSettingsReady &&
    (!isRadiologist || isUserPreferencesReady);

  const targetVersion = resolveAppVersion({
    role,
    userPreference: preferredVersion,
    organizationVersion: getOrganizationAppVersion(organizationSettings),
  });

  const shouldRedirect =
    isResolved && allowAutomaticAppSwitch(targetVersion) && canRedirectTo(targetVersion);

  useEffect(() => {
    if (shouldRedirect) {
      // Carry the active organization: it is stored per origin, so the other
      // app could otherwise resolve the version for a different org and send
      // the user straight back.
      window.location.replace(buildAppSwitchUrl(targetVersion, { organizationId }));
    }
  }, [shouldRedirect, targetVersion, organizationId]);

  return shouldRedirect ? <FullScreenLoader /> : <>{children}</>;
};

export default AppVersionGuard;
